import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/feature/PageHeader";
import SectionHeading from "@/components/feature/SectionHeading";
import JsonLd from "@/components/feature/JsonLd";
import { usePageMeta } from "@/hooks/usePageMeta";
import { aboutStats, platformPrinciples, siteEmail, siteIntro, siteVision, testimonials } from "@/mocks/site";

const pillars = [
  {
    icon: "ri-focus-3-line",
    title: "平台定位",
    desc: "不做流量内容站，只做直播争议的受理与公示基础设施。以事件为单位建档，让每一条线索都有唯一的受理轨迹。",
  },
  {
    icon: "ri-route-line",
    title: "受理理念",
    desc: "先取证、再定性、后公示。平台只对可核验的事实作出结论，不替任何一方站台，也不为情绪化表达背书。",
  },
  {
    icon: "ri-book-open-line",
    title: "内容原则",
    desc: "公示内容以事件概述与核查结论为主，隐藏个人信息，保留时间线，让外部可以独立复盘整起事件的来龙去脉。",
  },
];

const milestones = [
  { year: "起步期", title: "建立举报与受理基础流程", desc: "上线直播举报通道，明确线索受理标准与初步时效承诺。" },
  { year: "成长期", title: "推出争议事件档案与时间线", desc: "为每起事件建立独立档案，公开立案时间与核查进度。" },
  { year: "完善期", title: "上线处理结果公示与双向申诉", desc: "处置结果对外公示，同时为被举报方提供说明与反证入口。" },
  { year: "持续优化", title: "强化分类筛选与数据回看", desc: "按分类、状态与月份组织公示数据，方便长期复盘与检索。" },
];

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "爆料直播",
  email: siteEmail,
  slogan: siteVision,
  description: siteIntro,
};

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于爆料直播",
  description: "爆料直播的平台定位、受理理念、内容原则与联系方式。",
  inLanguage: "zh-CN",
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页" },
    { "@type": "ListItem", position: 2, name: "关于我们" },
  ],
};

export default function About() {
  usePageMeta({
    title: "关于我们 - 平台定位与受理原则 | 爆料直播",
    description:
      "了解爆料直播的平台定位、受理理念与内容原则。我们专注直播争议举报与处理公示，提供直播举报通道、主播违规投诉与平台处理结果同步，坚持事实优先、隐私保护与双向申诉。",
    keywords: "爆料直播,直播举报通道,直播争议事件,平台处理结果",
    path: "/about",
  });

  return (
    <SiteLayout>
      <JsonLd id="ld-about-org" data={contactSchema} />
      <JsonLd id="ld-about-page" data={aboutSchema} />
      <JsonLd id="ld-about-breadcrumb" data={breadcrumbSchema} />

      <PageHeader
        eyebrow="关于我们"
        title="关于爆料直播：让直播争议公开可查"
        description="我们是一支关注直播行业治理的团队，把观众遇到的画面中断、主播违规、消费纠纷与版权争议，整理成可以跟踪、可以公示、可以复盘的公开档案。"
        crumbs={[{ label: "首页", path: "/" }, { label: "关于我们" }]}
      />

      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="w-full h-64 md:h-80 lg:h-[420px] rounded-lg overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20organized%20event%20files%20and%20timeline%20nodes%20on%20a%20warm%20cream%20paper%20background%2C%20vermilion%20and%20deep%20teal%20accents%2C%20minimal%20geometric%20composition%2C%20subtle%20grain%2C%20no%20text&width=1200&height=1000&seq=about-brand-01&orientation=portrait"
                alt="爆料直播 直播争议档案与时间线示意"
                title="爆料直播 - 直播争议事件档案与处理公示"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="品牌介绍" title="为什么会有爆料直播" />
            <p className="mt-5 text-sm md:text-base leading-7 text-foreground-700">{siteIntro}</p>
            <p className="mt-4 text-sm md:text-base leading-7 text-foreground-700">
              直播是即时发生的，争议往往也在几秒内被刷走。我们希望把这些易逝的片段沉淀下来，用统一的标准受理、核查与公示，让平台处理结果不再是只有当事人才知道的内部消息。
            </p>
            <p className="mt-6 font-heading text-lg md:text-xl font-bold text-primary-700">“{siteVision}”</p>

            <dl className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutStats.map((stat) => (
                <div key={stat.label} className="bg-background-100 border border-background-200 rounded-lg px-4 py-4">
                  <dd className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                    {stat.value}
                    <span className="ml-1 text-xs font-medium text-foreground-500">{stat.note}</span>
                  </dd>
                  <dt className="mt-1.5 text-xs text-foreground-600">{stat.label}</dt>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100 border-y border-background-200">
        <SectionHeading
          eyebrow="发展理念"
          title="定位、理念与内容原则"
          description="三条不随版本变化的底线，决定了我们如何受理线索、如何核查事实，以及如何对外公示。"
        />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {pillars.map((item, index) => (
            <div key={item.title} className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
              <span
                className={`w-11 h-11 flex items-center justify-center rounded-lg ${
                  index === 1 ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-700"
                }`}
              >
                <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>

        <h3 className="mt-14 font-heading text-lg md:text-xl font-bold text-foreground-950">内容与受理原则</h3>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {platformPrinciples.map((item) => (
            <div key={item.title} className="bg-background-50 border border-background-200 rounded-lg p-5">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
              </span>
              <h4 className="mt-3 text-base font-medium text-foreground-950">{item.title}</h4>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="发展历程" title="从一条通道，到一套公示机制" />
          </div>
          <ol className="lg:col-span-8 border-l border-background-300 pl-5 md:pl-8 space-y-8">
            {milestones.map((item) => (
              <li key={item.year} className="relative">
                <span
                  className="absolute -left-[27px] md:-left-[39px] top-1.5 w-2.5 h-2.5 rounded-full bg-accent-500 ring-4 ring-accent-100"
                  aria-hidden="true"
                ></span>
                <p className="font-heading text-sm font-bold text-accent-700">{item.year}</p>
                <h3 className="mt-1 text-base md:text-lg font-medium text-foreground-950">{item.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100 border-y border-background-200">
        <SectionHeading
          align="center"
          eyebrow="用户口碑"
          title="他们这样评价爆料直播"
          description="来自观众、主播与行业观察者的反馈，是我们持续优化核查流程的依据。"
        />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {testimonials.map((item) => (
            <blockquote
              key={item.name}
              className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6"
            >
              <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true"></i>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">{item.content}</p>
              <footer className="mt-4 pt-4 border-t border-background-200 flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-800 font-heading font-bold">
                  {item.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </span>
              </footer>
            </blockquote>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center bg-background-100 border border-background-200 rounded-lg p-6 md:p-10">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="联系我们" title="有线索，也有话想说" />
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
              举报线索请优先通过举报通道提交，便于自动生成受理编号并进入核查流程。其他合作、媒体采访或流程建议，欢迎直接来信。
            </p>
            <ul className="mt-6 space-y-3 text-sm text-foreground-700">
              <li className="flex items-center gap-2.5">
                <i className="ri-mail-line text-primary-500" aria-hidden="true"></i>
                联系邮箱：<span className="text-foreground-950">{siteEmail}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-time-line text-primary-500" aria-hidden="true"></i>
                受理时间：每日 09:00 – 22:00
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-shield-check-line text-primary-500" aria-hidden="true"></i>
                举报人信息严格保密，未获授权不对外披露
              </li>
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/report"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-live-line" aria-hidden="true"></i>
                进入举报通道
              </Link>
              <Link
                to="/results"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-medium border border-foreground-300 text-foreground-900 hover:bg-background-50 transition-colors whitespace-nowrap"
              >
                查看处理结果公示
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20an%20envelope%20and%20shielding%20hands%20on%20a%20warm%20cream%20paper%20background%2C%20vermilion%20and%20amber%20accents%2C%20minimal%20geometric%20composition%2C%20subtle%20grain%2C%20no%20text&width=1200&height=900&seq=about-contact-02&orientation=landscape"
                alt="爆料直播 联系方式与信息保密示意"
                title="爆料直播 - 联系与信息保密"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}